import {
  cancelBookingPaymentAttempts,
  getBookingPaymentStatus,
} from '@/features/purchase/purchaseService';
import { STORAGE_KEYS, useStorage } from '@/hooks/useStorage';
import type { BookingDTO } from '@/types/dto/agent';
import type { PurchaseFlowBookingStorageData } from '@/types/storage';
import { getBookingFulfillments } from '@/utils/booking';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 7;

interface UsePaymentStatusCheckParams {
  booking: BookingDTO | null;
  enabled?: boolean;
}

export const usePaymentStatusCheck = ({
  booking,
  enabled = true,
}: UsePaymentStatusCheckParams) => {
  const navigate = useNavigate();
  const { setValue: setCheckoutSuccessSessionStorage } =
    useStorage<PurchaseFlowBookingStorageData>(
      sessionStorage,
      STORAGE_KEYS.CHECKOUT_SUCCESS_DATA
    );

  useEffect(() => {
    if (!enabled || !booking) return;

    const bookingId = booking.id;
    const bookingCode = booking.code;

    if (!bookingId || !bookingCode) return;

    let isCancelled = false;

    const pollPaymentStatus = async (
      id: string,
      maxAttempts: number
    ): Promise<string | undefined> => {
      let attempts = 0;

      while (attempts < maxAttempts && !isCancelled) {
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
        attempts++;

        try {
          const currentStatus = await getBookingPaymentStatus(id);
          if (currentStatus !== 'pending') {
            return currentStatus;
          }
        } catch {
          break;
        }
      }

      return 'pending';
    };

    const navigateToSuccess = () => {
      setCheckoutSuccessSessionStorage({
        bookingId,
        code: bookingCode,
        payerEmail: booking.purchaser?.contactInformation?.emailAddress ?? '',
        fulfillments: getBookingFulfillments(booking),
      });

      navigate('/success', { replace: true });
    };

    const handlePaymentStatus = async () => {
      try {
        const initialStatus = await getBookingPaymentStatus(bookingId);

        if (isCancelled) return;

        if (initialStatus === 'paid') {
          navigateToSuccess();
          return;
        }

        // Only poll and potentially cancel if there's a pending payment
        if (initialStatus === 'pending') {
          const finalStatus = await pollPaymentStatus(
            bookingId,
            MAX_POLL_ATTEMPTS
          );

          if (isCancelled) return;

          if (finalStatus === 'paid') {
            navigateToSuccess();
            return;
          }

          // Payment is still pending after max attempts - cancel it
          if (finalStatus === 'pending') {
            await cancelBookingPaymentAttempts(bookingId);
          }
        }

        // If 'not_paid', do nothing - user hasn't initiated payment yet
      } catch (error) {
        console.error('Error checking payment status:', error);
      }
    };

    void handlePaymentStatus();

    return () => {
      isCancelled = true;
    };
  }, [
    booking,
    booking?.id,
    booking?.code,
    enabled,
    navigate,
    setCheckoutSuccessSessionStorage,
  ]);
};
